// AdminRecurringEvents.jsx - Tekrarlayan ders yönetimi
import React, { useState, useEffect } from 'react';
import { getRecurringEvents, createRecurringEvent, updateRecurringEvent, deleteRecurringEvent, getRooms } from '../../utils/newApi';
import Card from '../common/Card';
import Button from '../common/Button';

const DAYS_OF_WEEK = [
  { value: 1, label: 'Pazartesi' },
  { value: 2, label: 'Salı' },
  { value: 3, label: 'Çarşamba' },
  { value: 4, label: 'Perşembe' },
  { value: 5, label: 'Cuma' },
  { value: 6, label: 'Cumartesi' },
  { value: 0, label: 'Pazar' }
];

const AdminRecurringEvents = () => {
  const [events, setEvents] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    roomId: '',
    dayOfWeek: 1,
    startTime: '09:00',
    endTime: '10:00',
    title: '',
    description: '',
    isActive: true
  });

  // Load events and rooms
  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [eventsData, roomsData] = await Promise.all([
        getRecurringEvents(),
        getRooms()
      ]);
      setEvents(eventsData);
      setRooms(roomsData);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message || 'Veriler yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    try {
      if (editingEvent) {
        await updateRecurringEvent(editingEvent.id, formData);
        setMessage('Ders güncellendi.');
      } else {
        await createRecurringEvent(formData);
        setMessage('Ders oluşturuldu.');
      }

      // Reset form
      setFormData({
        roomId: '',
        dayOfWeek: 1,
        startTime: '09:00',
        endTime: '10:00',
        title: '',
        description: '',
        isActive: true
      });
      setShowForm(false);
      setEditingEvent(null);

      // Reload
      await loadData();
    } catch (err) {
      console.error('Error saving event:', err);
      setError(err.message || 'Ders kaydedilirken hata oluştu.');
    }
  };

  // Handle edit
  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      roomId: event.roomId,
      dayOfWeek: event.dayOfWeek,
      startTime: event.startTime,
      endTime: event.endTime,
      title: event.title,
      description: event.description || '',
      isActive: event.isActive
    });
    setShowForm(true);
  };

  // Handle delete
  const handleDelete = async (eventId) => {
    if (!window.confirm('Bu dersi silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      setError('');
      setMessage('');
      await deleteRecurringEvent(eventId);
      setMessage('Ders silindi.');
      await loadData();
    } catch (err) {
      console.error('Error deleting event:', err);
      setError(err.message || 'Ders silinirken hata oluştu.');
    }
  };

  // Handle toggle active
  const handleToggleActive = async (event) => {
    try {
      setError('');
      setMessage('');
      await updateRecurringEvent(event.id, { isActive: !event.isActive });
      setMessage(`Ders ${!event.isActive ? 'aktif' : 'pasif'} edildi.`);
      await loadData();
    } catch (err) {
      console.error('Error toggling event:', err);
      setError(err.message || 'Durum değiştirilirken hata oluştu.');
    }
  };

  const getDayName = (dayOfWeek) => {
    return DAYS_OF_WEEK.find(d => d.value === dayOfWeek)?.label || dayOfWeek;
  };

  const getRoomName = (roomId) => {
    return rooms.find(r => r.roomId === roomId)?.name || roomId;
  };

  if (loading) {
    return (
      <Card>
        <div className="text-center py-12">
          <div className="loader mx-auto mb-4"></div>
          <p className="text-gray-200">Dersler yükleniyor...</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <svg className="w-8 h-8 text-blue-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h2 className="text-2xl font-bold text-white">Tekrarlayan Dersler</h2>
        </div>
        <Button
          onClick={() => {
            setShowForm(!showForm);
            setEditingEvent(null);
            setFormData({
              roomId: '',
              dayOfWeek: 1,
              startTime: '09:00',
              endTime: '10:00',
              title: '',
              description: '',
              isActive: true
            });
          }}
          className="bg-blue-600 hover:bg-blue-500"
        >
          {showForm ? 'İptal' : '+ Yeni Ders Ekle'}
        </Button>
      </div>

      {/* Messages */}
      {message && (
        <div className="bg-green-900/50 border border-green-500 text-green-200 px-4 py-3 rounded-lg">
          {message}
        </div>
      )}

      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Form */}
      {showForm && (
        <Card>
          <h3 className="text-xl font-bold text-blue-200 mb-4">
            {editingEvent ? 'Ders Düzenle' : 'Yeni Ders Ekle'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Oda */}
              <div>
                <label className="block text-sm font-semibold text-gray-200 mb-2">Oda</label>
                <select
                  value={formData.roomId}
                  onChange={(e) => setFormData({ ...formData, roomId: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                  required
                >
                  <option value="">Oda Seçiniz</option>
                  {rooms.map((room) => (
                    <option key={room.roomId} value={room.roomId}>
                      {room.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Gün */}
              <div>
                <label className="block text-sm font-semibold text-gray-200 mb-2">Gün</label>
                <select
                  value={formData.dayOfWeek}
                  onChange={(e) => setFormData({ ...formData, dayOfWeek: Number(e.target.value) })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                  required
                >
                  {DAYS_OF_WEEK.map((day) => (
                    <option key={day.value} value={day.value}>
                      {day.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Başlangıç Saati */}
              <div>
                <label className="block text-sm font-semibold text-gray-200 mb-2">Başlangıç Saati</label>
                <input
                  type="time"
                  value={formData.startTime}
                  onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                  required
                />
              </div>

              {/* Bitiş Saati */}
              <div>
                <label className="block text-sm font-semibold text-gray-200 mb-2">Bitiş Saati</label>
                <input
                  type="time"
                  value={formData.endTime}
                  onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                  required
                />
              </div>
            </div>

            {/* Başlık */}
            <div>
              <label className="block text-sm font-semibold text-gray-200 mb-2">Ders Adı</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Örn: Gitar Dersi"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                required
              />
            </div>

            {/* Açıklama */}
            <div>
              <label className="block text-sm font-semibold text-gray-200 mb-2">Açıklama (Opsiyonel)</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Ders hakkında ek bilgi..."
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                rows={3}
              />
            </div>

            {/* Aktif */}
            <div>
              <label className="flex items-center text-white">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="mr-2"
                />
                Aktif (Rezervasyon engellensin)
              </label>
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-500">
                {editingEvent ? 'Güncelle' : 'Oluştur'}
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingEvent(null);
                }}
                className="flex-1 bg-gray-600 hover:bg-gray-500"
              >
                İptal
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Events List */}
      {events.length === 0 && !showForm && (
        <Card>
          <div className="text-center py-12">
            <svg className="w-16 h-16 mx-auto text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-gray-200 text-lg">Henüz tekrarlayan ders eklenmemiş.</p>
          </div>
        </Card>
      )}

      {/* Events grouped by day */}
      {DAYS_OF_WEEK.map((day) => {
        const dayEvents = events.filter(e => e.dayOfWeek === day.value);
        if (dayEvents.length === 0) return null;

        return (
          <Card key={day.value}>
            <h3 className="text-xl font-bold text-blue-200 mb-4">{day.label}</h3>
            <div className="space-y-3">
              {dayEvents.map((event) => (
                <div
                  key={event.id}
                  className={`
                    p-4 rounded-lg border transition-all
                    ${event.isActive 
                      ? 'bg-blue-900/20 border-blue-700' 
                      : 'bg-gray-800/20 border-gray-700 opacity-60'
                    }
                  `}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="text-lg font-semibold text-white">{event.title}</h4>
                        <span className={`
                          px-2 py-1 rounded text-xs font-semibold
                          ${event.isActive ? 'bg-green-600' : 'bg-gray-600'}
                        `}>
                          {event.isActive ? 'Aktif' : 'Pasif'}
                        </span>
                      </div>
                      <div className="flex gap-4 text-sm text-gray-200">
                        <span>📍 {getRoomName(event.roomId)}</span>
                        <span>🕐 {event.startTime} - {event.endTime}</span>
                      </div>
                      {event.description && (
                        <p className="text-sm text-gray-300 mt-2">{event.description}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleToggleActive(event)}
                        className="px-3 py-2 bg-yellow-600 hover:bg-yellow-500 text-white rounded transition-colors"
                        title={event.isActive ? 'Pasif Yap' : 'Aktif Yap'}
                      >
                        {event.isActive ? '⏸' : '▶'}
                      </button>
                      <button
                        onClick={() => handleEdit(event)}
                        className="px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded transition-colors"
                      >
                        ✎
                      </button>
                      <button
                        onClick={() => handleDelete(event.id)}
                        className="px-3 py-2 bg-red-600 hover:bg-red-500 text-white rounded transition-colors"
                      >
                        🗑
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        );
      })}
    </div>
  );
};

export default AdminRecurringEvents;
